import {
    AfterViewInit,
    Directive,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChange
} from '@angular/core';

// external libs
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

// models
import { MarkerClusterFilter } from '../models/marker-cluster-filter.model';

// enums
import { ChangesMap } from '../enums/changes-map.enum';

// services
import { MapManager } from '../manager/map-manager';
import { H21MapClusterService } from '../services/h21-map-marker-cluster-service';
import { MarkerClusterEventService } from '../services/h21-map-marker-cluster-event.service';
import { MarkerClusterLoadService } from '../services/h21-map-marker-cluster-load.service';

@Directive({
    selector: '[h21MapMarkerCluster]',
    providers: [H21MapClusterService, MarkerClusterEventService, MarkerClusterLoadService],
})
export class H21MapMarkerClusterDirective implements AfterViewInit, OnChanges, OnDestroy {

    @Input() public apiUrl = 'https://h21-hotels-be-api.azurewebsites.net/api/';
    @Input() public changesMap = ChangesMap.bbox;
    @Input() public minClusterSize = 3;
    @Input() public maxZoom = 10;
    @Input() public filter: MarkerClusterFilter = null;

    @Output() public clusterClick: Subject<MouseEvent> = new Subject<MouseEvent>();
    @Output() public loading: Subject<boolean> = new Subject<boolean>();
    @Output() public markerClick: Subject<MouseEvent> = new Subject<MouseEvent>();
    @Output() public markerMouseOut: Subject<MouseEvent> = new Subject<MouseEvent>();
    @Output() public markerMouseOver: Subject<MouseEvent> = new Subject<MouseEvent>();
    @Output() public ready: Subject<void> = new Subject<void>();
    @Output() public countLoadMarkers: Subject<number> = new Subject<number>();

    public geoJson = null;

    private _addService: boolean;
    private _destroy$ = new Subject<boolean>();

    constructor(
        private _manager: MapManager,
        private _zone: NgZone,
        private _clusterService: H21MapClusterService) { }

    public ngAfterViewInit(): void {
        this._manager.getMap().loadMap$
            .pipe(
                filter(Boolean),
                takeUntil(this._destroy$),
            )
            .subscribe({
                next: () => { this._zone.run(() => this.onInit()); },
            });
    }

    public onInit(): void {
        if (!this._manager.getMap().markerCluster) { return; }
        if (this._addService) { return; }
        this._manager.getMap().markerCluster.addMarkerCluster(this);
        this._addService = true;
        this._addEventListeners();
        this.onInitEventListeners();
        this._clusterService.initService(this._manager, this);
    }

    public onInitEventListeners(): void {
        this._manager.getMap().markerCluster.addListener
            .pipe(
                filter(Boolean),
                takeUntil(this._destroy$),
            )
            .subscribe({
                next: () => { this._zone.run(() => this._addEventListeners()); },
            });
    }

    public ngOnChanges(changes: { [key: string]: SimpleChange }): void {
        if (!this._addService) { return; }
        changes.changesMap && this._clusterService.setChangeMap(this);
        changes.filter && this._manager.getMap().markerCluster.setFilter(this.filter);
        changes.maxZoom && this._clusterService.setChangeMap(this);
    }

    public ngOnDestroy(): void { this.onDestroy(); }

    public onDestroy(): void {
        this._destroy$.next(true);
        this._destroy$.complete();
        this._manager.getMap().markerCluster.removeMarkerCluster(this);
        this._clusterService.destroy();
        this._addService = false;
    }

    private _addEventListeners(): void {
        this._manager.getMap().markerCluster.createEventMouseClick(this)
            .pipe(takeUntil(this._destroy$))
            .subscribe((event: any) => {
                const eventAction = event.type === 'Cluster';
                eventAction ? this.clusterClick.next(event) : this.markerClick.next(event);
            });

        this._manager.getMap().markerCluster.createEventMouseOver(this)
            .pipe(takeUntil(this._destroy$))
            .subscribe((event: any) => {
                if (event.type === 'Cluster') { return; }
                this.markerMouseOver.next(event);
            });

        this._manager.getMap().markerCluster.createEventMouseOut(this)
            .pipe(takeUntil(this._destroy$))
            .subscribe((event: any) => {
                this.markerMouseOut.next(event);
            });

        this._manager.getMap().markerCluster.ready$
            .pipe(takeUntil(this._destroy$))
            .subscribe((event: any) => {
                this.ready.next();
            });

        this._manager.getMap().markerCluster.countLoadMarkers$
            .pipe(takeUntil(this._destroy$))
            .subscribe((count: number) => {
                this.countLoadMarkers.next(count);
            });

    }

}
